import React, { useMemo, useState } from "react";
import { Box, Typography, Slider, TextField } from "@mui/material";
import { C } from "../constants";
import { FONT } from "../theme/tokens";
import { FIELD_TONES } from "./FormControls";
import { parseMultiValue } from "./ProfileSuggestionChips";

export const ASSESS_RATING_KEYS = [
  "capability",
  "difficulties",
  "interest",
  "dislikes",
  "domain",
];

const STAGES = [
  { n: 0, label: "—" },
  { n: 1, label: "Beginner" },
  { n: 2, label: "Getting started" },
  { n: 3, label: "Practising" },
  { n: 4, label: "Confident" },
  { n: 5, label: "Strong" },
];

const VARIANT = {
  capability: {
    kicker: "Then rate",
    title: "How strong is each pick?",
    hint: "Tap a number. Slide how far along you are. Add a note only if you want extra context.",
    level: "Level",
    depth: "How far along",
    low: "Just started",
    high: "Solid",
    notesPh: "Optional — e.g. led two lab demos, still rusty on edge cases",
    stage: STAGES,
  },
  difficulties: {
    kicker: "Then rate",
    title: "How much does each one slow you?",
    hint: "Tap a number. Slide how often it blocks you. Notes are optional.",
    level: "Level",
    depth: "How blocking",
    low: "Rare",
    high: "Often",
    notesPh: "Optional — e.g. recursion only clicks after a worked example",
    stage: [
      { n: 0, label: "—" },
      { n: 1, label: "Mild" },
      { n: 2, label: "Occasional" },
      { n: 3, label: "Regular" },
      { n: 4, label: "Frequent" },
      { n: 5, label: "Major" },
    ],
  },
  interest: {
    kicker: "Then rate",
    title: "How much do you enjoy each one?",
    hint: "Tap a number. Slide how much to use it in Mini Build. Notes are optional.",
    level: "Level",
    depth: "How much to use",
    low: "Light",
    high: "A lot",
    notesPh: "Optional — e.g. use cricket scoring as a data example",
    stage: [
      { n: 0, label: "—" },
      { n: 1, label: "Mild" },
      { n: 2, label: "Enjoy" },
      { n: 3, label: "Favourite" },
      { n: 4, label: "Core hobby" },
      { n: 5, label: "Identity" },
    ],
  },
  dislikes: {
    kicker: "Then rate",
    title: "How strongly should we skip each one?",
    hint: "Tap a number. Slide how hard to avoid. Notes can say a tiny step you still accept.",
    level: "Level",
    depth: "How hard to skip",
    low: "Soft skip",
    high: "Hard no",
    notesPh: "Optional — e.g. skip 40-min lectures; 4-min recap is OK",
    stage: [
      { n: 0, label: "—" },
      { n: 1, label: "Mild" },
      { n: 2, label: "Prefer skip" },
      { n: 3, label: "Strong" },
      { n: 4, label: "Drain" },
      { n: 5, label: "Hard no" },
    ],
  },
  domain: {
    kicker: "Then rate",
    title: "How committed are you to each path?",
    hint: "Tap a number. Slide how serious this direction is. Notes are optional.",
    level: "Level",
    depth: "How serious",
    low: "Curious",
    high: "Primary",
    notesPh: "Optional — e.g. FinTech internships first; full-stack if the product is clear",
    stage: [
      { n: 0, label: "—" },
      { n: 1, label: "Curious" },
      { n: 2, label: "Exploring" },
      { n: 3, label: "Serious" },
      { n: 4, label: "Committed" },
      { n: 5, label: "Primary" },
    ],
  },
};

const TONE_BY_KIND = {
  capability: "teal",
  difficulties: "slate",
  interest: "coral",
  dislikes: "coral",
  domain: "amber",
};

function clampStars(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, Math.round(n)));
}

function clampProgress(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function emptyItemRatings() {
  return {
    capability: {},
    difficulties: {},
    interest: {},
    dislikes: {},
    domain: {},
  };
}

export function normalizeItemRatings(src = {}) {
  const next = emptyItemRatings();
  const nested = src.itemRatings && typeof src.itemRatings === "object" ? src.itemRatings : {};
  ASSESS_RATING_KEYS.forEach((k) => {
    next[k] = nested[k] && typeof nested[k] === "object" ? { ...nested[k] } : {};
  });
  if (src.skillRatings && typeof src.skillRatings === "object") {
    next.capability = { ...src.skillRatings, ...next.capability };
  }
  return next;
}

export function pruneSkillRatings(skillsText, ratings) {
  const keep = new Set(parseMultiValue(skillsText).map((s) => s.toLowerCase()));
  const next = {};
  Object.entries(ratings || {}).forEach(([k, v]) => {
    if (keep.has(String(k).toLowerCase())) next[k] = v;
  });
  return next;
}

export function formatRatedItems(ratings) {
  return Object.entries(ratings || {})
    .map(([skill, v]) => {
      const stars = Number(v?.stars) || 0;
      const progress = Number(v?.progress) || 0;
      const notes = String(v?.notes || "").trim();
      if (!stars && !progress && !notes) return "";
      let s = `${skill}: ${stars}/5, ${progress}%`;
      if (notes) s += ` — note: ${notes}`;
      return s;
    })
    .filter(Boolean)
    .join("; ");
}

function LevelPills({ value, onChange, accent, stages }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexWrap: "nowrap" }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = value === n;
        return (
          <button
            key={n}
            type="button"
            title={stages[n]?.label || `${n}`}
            onClick={() => onChange(value === n ? 0 : n)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: `1.5px solid ${on ? accent : C.border}`,
              background: on ? accent : "#fff",
              color: on ? "#fff" : C.muted,
              fontSize: 12,
              fontWeight: 700,
              fontFamily: FONT.family,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            {n}
          </button>
        );
      })}
    </Box>
  );
}

function RatingRow({
  label,
  row,
  copy,
  tone,
  noteOpen,
  onToggleNote,
  onPatch,
}) {
  const stars = clampStars(row.stars);
  const progress = clampProgress(row.progress);
  const notes = String(row.notes || "");
  const stage = copy.stage[stars] || copy.stage[0];
  const done = Boolean(stars || progress || notes.trim());

  return (
    <Box
      sx={{
        px: { xs: 1.1, sm: 1.35 },
        py: 1.15,
        bgcolor: done ? "#fff" : "transparent",
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.15fr) auto minmax(160px, 1.1fr) auto" },
          gap: { xs: 0.85, sm: 1.25 },
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: C.text,
              letterSpacing: FONT.letterSpacing,
              lineHeight: 1.3,
            }}
          >
            {label}
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 650, color: C.muted, mt: 0.2 }}>
            {stars ? stage.label : "Not rated yet"}
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              display: { xs: "block", sm: "none" },
              fontSize: 10,
              fontWeight: 700,
              color: C.faint,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mb: 0.4,
            }}
          >
            {copy.level}
          </Typography>
          <LevelPills
            value={stars}
            onChange={(n) => onPatch({ stars: n })}
            accent={tone.accent}
            stages={copy.stage}
          />
        </Box>

        <Box sx={{ minWidth: 0, px: { sm: 0.5 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: -0.4 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.faint }}>{copy.low}</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: C.text }}>{progress}%</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.faint }}>{copy.high}</Typography>
          </Box>
          <Slider
            size="small"
            value={progress}
            onChange={(_, v) => onPatch({ progress: Array.isArray(v) ? v[0] : v })}
            sx={{
              color: tone.accent,
              py: 0.6,
              "& .MuiSlider-thumb": { width: 14, height: 14 },
              "& .MuiSlider-rail": { opacity: 0.28 },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
          <button
            type="button"
            onClick={onToggleNote}
            style={{
              border: `1.5px solid ${notes.trim() || noteOpen ? tone.accent : C.border}`,
              background: notes.trim() || noteOpen ? tone.soft : "#fff",
              color: notes.trim() || noteOpen ? tone.chip : C.muted,
              borderRadius: 999,
              padding: "6px 11px",
              fontSize: 11.5,
              fontWeight: 700,
              fontFamily: FONT.family,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {notes.trim() ? "Edit note" : noteOpen ? "Hide note" : "Add note"}
          </button>
        </Box>
      </Box>

      {noteOpen ? (
        <Box sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            multiline
            minRows={2}
            maxRows={4}
            placeholder={copy.notesPh}
            value={notes}
            onChange={(e) => onPatch({ notes: String(e.target.value).slice(0, 280) })}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: FONT.family,
                borderRadius: 2,
              },
            }}
          />
          <Typography sx={{ fontSize: 10.5, fontWeight: 650, color: C.faint, mt: 0.4 }}>
            {notes.length}/280 · optional
          </Typography>
        </Box>
      ) : notes.trim() ? (
        <Typography
          sx={{
            mt: 0.75,
            fontSize: 12.5,
            fontWeight: 650,
            color: C.muted,
            lineHeight: 1.45,
            pl: 0.2,
          }}
        >
          “{notes.trim()}”
        </Typography>
      ) : null}
    </Box>
  );
}

/** Compact rate-your-picks panel. Import never fills this — chips come first. */
export default function SkillRatingsEditor({
  skills = "",
  ratings = {},
  onChange,
  kind = "capability",
  title,
  hint,
}) {
  const list = parseMultiValue(skills);
  const [open, setOpen] = useState(true);
  const [openNote, setOpenNote] = useState("");

  const copy = VARIANT[kind] || VARIANT.capability;
  const tone = FIELD_TONES[TONE_BY_KIND[kind] || "teal"];
  const map = ratings && typeof ratings === "object" ? ratings : {};

  const ratedCount = useMemo(
    () =>
      list.filter((label) => {
        const row = map[label] || {};
        return clampStars(row.stars) || clampProgress(row.progress) || String(row.notes || "").trim();
      }).length,
    [list, map]
  );

  if (!list.length) return null;

  const setOne = (label, patch) => {
    onChange?.({
      ...map,
      [label]: {
        stars: 0,
        progress: 0,
        notes: "",
        ...(map[label] || {}),
        ...patch,
      },
    });
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: 2.5,
        border: `1.5px solid ${open ? `${tone.accent}55` : C.border}`,
        overflow: "hidden",
        bgcolor: C.surface,
        fontFamily: FONT.family,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          px: 1.4,
          py: 1.15,
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          bgcolor: open ? tone.soft : "#fff",
          fontFamily: FONT.family,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: tone.chip, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {copy.kicker}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: C.text, letterSpacing: FONT.letterSpacing, lineHeight: 1.3 }}>
            {title || copy.title}
          </Typography>
          {!open ? (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mt: 0.2 }}>
              {ratedCount ? `${ratedCount} of ${list.length} rated` : "Closed — click to rate"}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mt: 0.25, lineHeight: 1.4 }}>
              {hint || copy.hint}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            px: 0.85,
            py: 0.35,
            borderRadius: 99,
            bgcolor: ratedCount === list.length ? C.successBg : "#fff",
            color: ratedCount === list.length ? "#0B7A54" : tone.chip,
            fontSize: 11,
            fontWeight: 800,
            border: `1px solid ${ratedCount === list.length ? "transparent" : C.border}`,
          }}
        >
          {ratedCount}/{list.length}
        </Box>
        <Box sx={{ flexShrink: 0, fontSize: 16, fontWeight: 700, color: C.muted, lineHeight: 1, pl: 0.25 }}>
          {open ? "▾" : "▸"}
        </Box>
      </Box>

      {open ? (
        <>
          <Box
            sx={{
              display: { xs: "none", sm: "grid" },
              gridTemplateColumns: "minmax(0, 1.15fr) auto minmax(160px, 1.1fr) auto",
              gap: 1.25,
              px: 1.35,
              py: 0.65,
              bgcolor: C.surfaceAlt,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: "0.07em", textTransform: "uppercase" }}>
              You picked
            </Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: "0.07em", textTransform: "uppercase" }}>
              {copy.level}
            </Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: "0.07em", textTransform: "uppercase" }}>
              {copy.depth}
            </Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: "0.07em", textTransform: "uppercase", textAlign: "right" }}>
              Note
            </Typography>
          </Box>

          {list.map((label) => (
            <RatingRow
              key={label}
              label={label}
              row={map[label] || {}}
              copy={copy}
              tone={tone}
              noteOpen={openNote === label}
              onToggleNote={() => setOpenNote((cur) => (cur === label ? "" : label))}
              onPatch={(patch) => setOne(label, patch)}
            />
          ))}
        </>
      ) : null}
    </Box>
  );
}
